<template>
  <div class="ms-combobox ms-editor" :class="[{'ms-validate': true}]">
    <div v-if="title && showTitle" class="combo-title flex">
      <div class="combo-title__text">{{ title }}</div>
    </div>
    <label v-if="label" :title="title" class="label">{{ label }}</label>
    <div
      class="flex-row combo-box-input"
      :class="[{error: errorMessage && !selectOnly, disabled: disabled}, hasBorder ? 'border' : '']"
    >
      <div v-if="leftIcon" :class="['icon24 left-icon', leftIcon]"></div>

      <div v-if="leftTitle" :class="['left-title']">{{ leftTitle }}</div>
      <!-- TODO => Thêm các prop để show tooltip -->

      <input
        ref="input"
        v-model="internalText"
        :title="internalText"
        class="input flex"
        :placeholder="placeholder"
        :disabled="disabled || isPreventEnterText"
        :readOnly="readOnly || selectOnly"
        :maxlength="maxLength"
        :class="{disabled: disabled}"
        v-on="listeners"
        @keydown.esc="onEsc"
        @keydown.enter="onEnter"
        @keydown.f3="quickSearchClick"
        @keydown.f9="showQuickAdd(false, true)"
      />

      <!-- Khi không config thêm nhanh nhiều object trong combobox -->
      <div
        v-if="quickAdd"
        class="quickAdd"
        :class="{hide: readOnly || disabled}"
        @click="showQuickAdd"
        @keydown.enter="showQuickAdd"
      ></div>

      <!-- Menu khi click icon thêm nhanh nhiều object trong combobox -->
      <menu-wrapper>
        <template #menu-button="{toggleMenu}">
          <!-- Khi có config thêm nhanh nhiều object trong combobox -->
          <div
            v-if="quickAddMulti"
            class="quickAdd quickAddMulti"
            :class="{
              disabled: disabled,
              'point-event-none': readOnly,
              hide: readOnly || disabled,
            }"
            @click="toggleMenu"
            @keydown.enter="toggleMenu"
          ></div>
        </template>
        <template #menu-content>
          <menu-item
            v-for="(item, index) in quickAddMulti"
            :key="index"
            @menu-item-click="showQuickAdd(false, false, item.valueField)"
            >{{ item.displayField }}</menu-item
          >
        </template>
      </menu-wrapper>

      <div
        class="icon-combo--dropdown"
        :class="{
          disabled: disabled,
          'point-event-none': readOnly,
          expand: expanded,
        }"
        @click="triggerClick"
      ></div>
    </div>

    <div v-if="errorMessage && isShowText && !isGridEditor && !selectOnly && !showOnlyErrorBorder" class="error-text">
      {{ errorMessage }}
    </div>

    <ms-combo-box-dropdown
      v-if="expanded"
      ref="dropdownPanel"
      :topx="dropdownTopx"
      :leftx="dropdownLeftx"
      :max-widthx="dropdownMaxWidthx"
      :min-widthx="dropdownMinWidthx"
      :columnx="columns"
      :combo-type="comboType"
      :highligh-index="typeAheadPointer"
      :scroll-top="dropdownScrollTop"
      :loading="loading"
      :quick-add="quickAdd"
      :quick-search="quickSearch"
      :customPosition="customPosition"
      :class="classDropdown"
      @scrollend="dropdownScrollEnd"
      @add-new="showQuickAdd"
    >
      <ms-combo-box-item
        v-for="(item, index) in internalDataSource"
        :key="item[valueField]"
        :combo-type="comboType"
        :item="item"
        :is-selected="item && item[valueField] == modelValue"
        :columnx="columns"
        :value-field="valueField"
        :display-field="displayField"
        :class="{'ms-combobox-item--highlight': typeAheadPointer == index}"
        :styleCellCustom="styleCellCustom"
        @mousedown="onItemClick(item, $event)"
      ></ms-combo-box-item>
      <template #quickSearch>
        <div
          v-if="quickSearch"
          class="quickSearch"
          :class="{hide: readOnly || disabled}"
          @click="quickSearchClick"
          @keydown.enter="quickSearchClick"
        >
          <div class="button icon24 quick-search"></div>
          <div class="txt-quick-search">Tìm nhanh (F3)</div>
        </div>
      </template>
    </ms-combo-box-dropdown>
  </div>
</template>
<script>
import {defineComponent, getCurrentInstance, ref} from 'vue';

import MsComboBoxDropdown from './MsComboBoxDropdown.vue';
import MsComboBoxItem from './MsComboBoxItem.vue';
import MenuItem from '@/components/dropdown/MenuItem.vue';
import MenuWrapper from '@/components/dropdown/MenuWrapper.vue';
import commonFn from '@/commons/commonFunction.js';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import EventBusGlobal, {GlobalEventName} from '@/commons/eventBusGlobal';
import _ from 'lodash';
import {filter} from '@/mixins/common/filter';
import {validateCombobox} from '@/mixins/component/validateCombobox';
import {useValidateControl} from '@/composables/validateControl.js';

import popupUtil from '@/commons/popupUtil.js';

export default defineComponent({
  name: 'MsCombobox',
  components: {
    MsComboBoxDropdown,
    MsComboBoxItem,
    MenuWrapper,
    MenuItem,
  },
  extends: msBaseComponent,
  mixins: [filter],
  props: {
    modelValue: {},
    customTooltip: {
      default: null,
      type: [Object],
    },
    placeholder: {
      default: null,
      type: [Number, String],
    },
    maxItemsDisplay: {
      default: 5,
      type: Number,
    },
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * Trường làm value
     * CreatedBy LTDAT 15.06.2020
     */
    valueField: {
      type: String,
      default: null,
    },
    /**
     * Trường làm text hiển thị dạng code - name
     * HTHIEP 01.07.2022
     */
    suffixDisplayField: {
      type: String,
      default: null,
    },
    /**
     * Trường giá trị ngăn cách
     * HTHIEP 01.07.2022
     */
    seperateChar: {
      type: String,
      default: '-',
    },
    /**
     * Trường làm text display
     * CreatedBy LTDAT 15.06.2020
     */
    displayField: {
      type: String,
      default: null,
    },
    // Dữ liệu của combobox
    data: {
      default: () => [],
      type: Array,
    },
    // Độ rộng của 1 item
    itemSize: {
      default: 28,
      type: Number,
    },
    // Chỉ nhận giá trị có trong danh sách
    forceSelection: {
      default: true,
      type: Boolean,
    },
    // Là combotree hay không
    isTree: {
      default: false,
      type: Boolean,
    },
    // Parent Field
    idProperty: {
      default: 'id',
      type: String,
    },
    // Field chứa parentID
    parentIdProperty: {
      default: 'parent_id',
      type: String,
    },
    // Không cho phép chọn trường là cha
    notSelectionParent: {
      default: false,
      type: Boolean,
    },
    // Độ rông của dropdown tính bằng px
    dropdownWidth: {
      default: null,
      type: [String, Number],
    },
    // Mode query lấy dữ liệu từ server hay client
    queryMode: {
      type: String,
      default: 'local',
    },
    remoteFilter: {
      type: Boolean,
      default: false,
    },
    // Gán giá trị text lúc ban đầu
    initText: {
      default: null,
      type: [Number, String],
    },
    // chỉ cho phép chọn không cho phép nhập text
    selectOnly: {
      default: false,
      type: Boolean,
    },
    autoLoad: {
      default: false,
      type: Boolean,
    },
    display: {},
    mappingField: {
      default: null,
    },
    firstExpandLoad: {
      default: false,
      type: Boolean,
    },
    keyField: {
      // default: '__vKeyValue',
      type: String,
    },
    maxLength: {
      default: null,
      type: Number,
    },
    leftIcon: {
      type: String,
    },
    /**
     * Câu hình quicksearch
     */
    quickSearch: {
      type: String,
    },
    /**
     * Dữ liệu truyền kèm quicksearch
     */
    quickSearchParam: {
      type: Object,
      default: null,
    },
    /**
     * Câu hình add
     */
    quickAdd: {
      type: String,
      default: null,
    },

    /**
     * Cấu hình add multi form
     */
    quickAddMulti: {
      type: Array,
      default: null,
    },

    isShowText: {
      type: Boolean,
      default: true,
    },
    /*
     * Hàm show ra câu thông báo riêng theo từng trường hợp
     * ví dụ: customWarring {rules:'required', mes: 'Tài sản này là của pvduy'}
     */
    customWarring: {
      type: Object,
      default: null,
    },
    /**
     * Danh sách các trường dữ liệu lấy về
     */
    requestField: {
      type: String,
      default: null,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    /**
     * Danh sách các trường dữ liệu lấy về
     */
    title: {
      type: String,
      default: null,
    },
    /**
     * custom lại tham số filter
     */
    buildFilter: {
      type: Function,
      default: null,
    },

    isReload: {
      type: Boolean,
      default: false,
    },
    /**
     * Luôn luôn gọi api khi mở combobox
     */
    forceReload: {
      type: Boolean,
      default: false,
    },
    /**
     * size paging
     */
    pageSize: {
      type: Number,
      default: 10,
    },
    /**
     * Giá trị bản ghi tất cả
     */
    selectAllValue: {
      type: [Number, String],
      default: -1,
    },
    /**
     * Text hiển thị tất cả
     */
    selectAllText: {
      type: String,
      default: null,
    },
    /**
     * Có hiển thị bản ghi tất cả không
     */
    showSelectAll: {
      type: Boolean,
      default: false,
    },
    // Xác định xem input có border hay không?
  hasBorder: {
      default: true,
      type: Boolean,
    },
    // Chỉ hiển thị border khi lỗi mà không hiển thị thông báo lỗi
    showOnlyErrorBorder: {
      default: false,
      type: Boolean,
    },
    // Xác định các rule validate
    // Mặc định của combo sẽ là force select
    rules: {
      default: [{name: 'forceSelection'}],
      type: Array,
    },

    // trường cho phép có được nhập text tự do trên combo hay không
    // cho phép bind dữ liệu vào combo nhưng không bind vào dropdown
    isFreeText: {
      default: false,
      type: Boolean,
    },
    /**
     * Combo chỉ chọn, ko được phép nhập
     */
    isPreventEnterText: {
      type: Boolean,
      default: false,
    },
    leftTitle: {
      type: String,
      default: null,
    },
    classDropdown: {
      type: String,
      default: null,
    },
    /**
     * Chặn event paste
     */
    preventPaste: {
      type: Boolean,
      default: false,
    },

    dataRow: {
      type: Object,
      default: null,
    },

    /**
     * Hàm kiểm tra xem dữ liệu binding vào combobox có phải là dữ liệu từ datasource hay không
     * NHThai 26.12.2024
     */
    checkHasDataSource: {
      type: Boolean,
      default: false,
    },
    customPosition: { // vcdoan 18.03.2025 - bổ sung để custom vị trí dropdown
      type: Function,
      default: null,
    },
    styleCellCustom: { // vcdoan 18.03.2025 - bổ sung để custom stylecell
      type: Function,
      default: null,
    },
    dropdownAlignLeft: { // vcdoan 18.03.2025 - cấu hình để dropdown align left
      type: Boolean,
      default: false,
    }
  },
  emits: [
    'msKeydown',
    'comboboxLoadData',
    'blur',
    'beforeQuery',
    'autoSelected',
    'update:modelValue',
    'update:suffixDisplay',
    'update:display',
    'selected',
    'beforeExpand',
    'change',
    'click',
    'input',
    'errorMessages',
    'selectItem',
  ],

  setup(props) {
    const {errorMessage, handlerValidateCombobox, getTitleError, clearValidate} = useValidateControl({
      props,
    });
    const {proxy} = getCurrentInstance();
    const validate = () => {
      if (!proxy.isGridEditor || proxy.forceValidate) {
        return handlerValidateCombobox();
      }
      return '';
    };

    const isShowFullText = ref(false);

    return {
      errorMessage,
      validate,
      getTitleError,
      clearValidate,
      isShowFullText,
    };
  },
  data() {
    const me = this;
    me.initStaticData();

    return {
      internalDataSource: [],
      internalText: null,
      expanded: false,
      dropdownTopx: 0,
      dropdownLeftx: 0,
      internalSelectedItem: null,
      typeAheadPointer: -1,
      focused: false,
      // ĐVThi  03/03/2021
      // kích thước mặc định của itemSize của combox khi được dùng trên lưới Editor
      gridItemSize: 48,
      tooltipLeft: 0,
      dropdownScrollTop: 0,
      /**
       * Đang load dữ liệu
       */
      loading: false,
      /**
       * Tham số load dữ liệu lần gần nhất
       */
      lastRequestParam: null,
      dropdownMinWidthx: null,
      dropdownMaxWidthx: null,
    };
  },
  computed: {
    /**
     * data source cho tooltip
     * created by ntphong 29/5/2021
     */
    tooltipDataSource() {
      const me = this;
      let dataSource = me.internalDataSource;
      if (me._allData && me._allData.length > 0) {
        dataSource = me._allData;
      }
      return dataSource;
    },
    listeners() {
      const me = this;
      return {
        input: event => me.onInput(event),
        focus: event => me.onFocus(event),
        change: event => me.onChange(event),
        blur: event => me.onBlur(event),
        keydown: event => me.onKeydown(event),
        click: event => me.onClick(event),
        paste: e => me.onPaste(e),
      };
    },
    // Tính độ rộng của dropdown combobox để scroll
    styleScroller() {
      const me = this;
      return {'max-height': me.maxItemsDisplay * me.itemSize + 'px'};
    },
  },

  watch: {
    data: {
      inmediate: true,
      handler(newVal) {
        const me = this;
        if (me.loading) {
          me.loading = false;

          me.$nextTick(() => {
            me.doHightlightItem();
          });
        }

        if (newVal == null) {
          // tình huống có thêm/sửa/xóa dữ liệu gốc -> reset để khi expand sẽ load lại dữ liệu
          me.lastQueryString = new Date().getTime();
          me._allData = null;
        } else {
          if (me.queryMode !== 'remote') {
            me._allData = newVal;
          }

          if (me.internalDataSource != newVal && !(me.internalDataSource.length === 0 && newVal.length === 0)) {
            me.initData();
          }
        }
      },
    },
    /**
     * Set lại giá trị hiển thị khi giá trị của biến initText thay đổi
     */
    initText: {
      immediate: true,
      handler(newVal, oldVal) {
        const me = this;
        if (newVal || oldVal) {
          me.setInitText(newVal);
        }
      },
    },
    /**
     * Set lại giá trị modelValue khi được truyền từ props vàos
     * Hàm này được chạy khi click chọn item trên combobox và binding lần đầu
     * NHThai 26.12.2024
     */
    modelValue: {
      immediate: true,
      handler(newVal, oldVal) {
        const me = this;
        if (newVal && me.checkHasDataSource) {
          let res = me.checkRecordExistsInDataSource();
          // Nếu không tồn tại => reset lại giá trị
          if (!res) {
            me.reset();

            if (me.data && me.data.length > 0) {
              me.setInternalItem(me.data[0]);
            }
          }
        }
      },
    },

    // Khi thay đổi số phần tử của combox thì thiết lập lại vị trí của dropdow
    internalDataSource: {
      immediate: true,
      handler() {
        // ĐVThi sửa lỗi 90491 không thay đổi vị trí khi gõ text để search trên combox
        this.changeDropdownPosition();
        this.doHightlightItem();
      },
    },

    // bắn errorMessage ra bên ngoài để sử dụng
    // dvhung2 19.03.2024
    errorMessage: {
      immediate: true,
      handler(newVal, old) {
        this.$emit('errorMessages', newVal);
      },
    },
  },
  // Hàm khởi tạo dữ liệu hook func
  created() {
    const me = this;
    me.initIndex = 0;

    me.initCombo();
    // me.setInitIndex();
    me.initData();
    if (me.autoLoad) {
      me.doQueryInternal(null, false);
    }
    EventBusGlobal.$on(GlobalEventName.scrollEvent, me.scrollEvent);
    // if (me.mappingField) {
    //   for (let key in me.mappingField) {
    //     me.$options.propsData[key] = null;
    //   }
    // }
    // me.initData();
  },
  mounted() {
    const me = this;
    me.reload = true;
    // Debounce query để delay load
    if (me.queryMode != 'remote') {
      me.internalQueryDelay = 0;
    }
    me.doQuery = _.debounce(me.doQueryInternal, me.internalQueryDelay);
    document.addEventListener('click', me.click_out);

    // init alltext
    me._selectAllText = me.selectAllText || me.$t('i18nComponent.Combobox.SelectAllItemText');
    me.doHightlightItem();
  },
  beforeUnmount() {
    const me = this;
    me.collapse();
    if (me.$refs.dropdownPanel) {
      me.$refs.dropdownPanel.$el.remove();
    }
    EventBusGlobal.$off(GlobalEventName.scrollEvent, me.scrollEvent);
    document.removeEventListener('click', me.click_out);
  },
  methods: {
    getTooltipClass() {
      const me = this;
      if (me.$el) {
        me.tooltipLeft = -me.$el.offsetWidth / 2;
      }
      if (me.customTooltip) {
        return 'tooltip-' + me.customTooltip.type.toLowerCase();
      } else {
        return '';
      }
    },
    getValue() {
      const me = this;
      return me.internalText;
    },
    /**
     * kiểm tra xem dữ liệu trên combobox có trống hay không
     * @author tmchi 16.04.2021
     */
    isNullOrEmptyvalue() {
      if (this.modelValue == null || this.modelValue == {}) {
        return true;
      } else {
        return false;
      }
    },
    /**
     * Bắt sự kiện khi scroll trên chương trình thì đóng combobox lại
     * Created by LTDAT
     */
    scrollEvent() {
      const me = this;
      if (me.expanded) {
        me.collapse();
      }
    },
    /**
     * Mặc định chọn giá trị theo index ban đầu
     * Created by LTDAT
     */
    setInitIndex() {
      const me = this;
      if (me.initIndex >= 0 && me.internalDataSource && me.internalDataSource.length > 0 && me.typeAheadPointer < 0) {
        me.typeAheadPointer = me.initIndex;
        me.setInternalItem(me.internalDataSource[me.typeAheadPointer], false);
      }
    },
    /**
     * Set giá trị hiện thị ban đầu cho combobox khi combo chưa có dữ liệu
     * Created by LTDAT(25.06.2020)
     */
    setInitText(val) {
      const me = this;
      me.internalText = me.initText ? me.initText : val;
    },
    /**
     * Thực hiện query dữ liệu, có thể từ local hoặc remote tùy theo cấu hình
     * CreatedBy LTDAT 11/12/2019
     */
    doQueryInternal(searchString, expand) {
      const me = this;
      const event = {preventDefault: false, sender: me};
      let queryString = searchString;
      me.$emit('beforeQuery', event);
      if (event.preventDefault) {
        return false;
      }

      // Đoạn này check xem nếu khác querystring thì thực hiện load data từ server
      if (me.lastQueryString == queryString && !me.reload && !me.forceReload) {
        if (expand) {
          me.expand();
        }
      } else {
        // Nếu có queryString thì thực hiện reset data về ban đầu
        if (queryString) {
          me.dropdownScrollTop = 0;
          me.internalDataSource = [];
          me.lastQueryString = queryString;
          me.querying = true;
        }

        if (me.queryMode === 'remote' && (!me._allData || me.forceReload)) {
          if (me.isTree) {
            queryString = null;
          }
          me.lastQueryString = queryString;
          me.doRemoteQuery(queryString);
        } else {
          me.$nextTick(() => {
            // HTHIEP 01.07.2022: Tạm trường hợp có subffix k hỗ trợ filter local
            me.doLocalQuery(queryString);
            if (expand && !me.expanded) {
              me.expand();
            } else {
              me.doHightlightItem(true);
            }
          });
        }
        me.reload = false;
      }
    },

    /**
     * Xử lý sau khi query xong dữ liệu
     * CreatedBy LTDAT 24.06.2020
     */
    afterQuery(expand) {
      const me = this;
      if (expand) {
        me.expand();
      }
    },
    /**
     * Query dữ liệu api
     * CreatedBy LTDAT 24.06.2020
     */
    doRemoteQuery(queryString) {
      // Build filter,
      const me = this;
      const filters = [];
      const filterFields = me.filterFields || {};
      return new Promise((resolve, reject) => {
        try {
          if (queryString) {
            const filterQuery = {};
            const fields = Object.keys(filterFields);
            // HTHIEP 01.07.2022: Xử lý query trường hợp có subffix
            if (queryString && me.suffixDisplayField && queryString.contains(me.seperateChar)) {
              const queryStrings = queryString.trim().split(me.seperateChar);
              const filter = {
                Field: fields[0],
                Operator: filterFields[fields[0]],
                Value: queryStrings[0].trim(),
              };
              filter.Ors = [];
              filters.push(filter);
              if (fields.length > 1) {
                for (let i = 1; i < fields.length; i++) {
                  filter.Ors.push({
                    Field: fields[i],
                    Operator: filterFields[fields[i]],
                    Value: queryStrings[0].trim(),
                  });
                }
              }
              for (let i = 1; i < queryStrings.length; i++) {
                const queryValue = queryStrings[i];
                if (queryValue) {
                  filter.Ors.push({
                    Field: fields[0],
                    Operator: filterFields[fields[0]],
                    Value: queryValue.trim(),
                  });
                  if (fields.length > 1) {
                    for (let i = 1; i < fields.length; i++) {
                      filter.Ors.push({
                        Field: fields[i],
                        Operator: filterFields[fields[i]],
                        Value: queryValue.trim(),
                      });
                    }
                  }
                }
              }
            } else {
              const filter = {
                Field: fields[0],
                Operator: filterFields[fields[0]],
                Value: queryString,
              };
              filters.push(filter);
              if (fields.length > 1) {
                filter.Ors = [];
                for (let i = 1; i < fields.length; i++) {
                  filter.Ors.push({
                    Field: fields[i],
                    Operator: filterFields[fields[i]],
                    Value: queryString,
                  });
                }
              }
            }
          }
          const payload = {};
          payload.skip = 0;
          payload.take = me.pageSize;
          payload.pageAppend = me.lastRequestParam ? me.lastRequestParam.pageAppend : false;
          if (filters.length > 0) {
            payload.filter = JSON.stringify(filters);
          }

          let columns = [me.displayField, me.valueField];
          if (me.columns && me.columns.length > 0) {
            me.columns.forEach(item => {
              if (item.dataField && columns.indexOf(item.dataField) === -1) {
                columns.push(item.dataField);
              }
            });
          }

          if (me.mappingField) {
            for (const i in me.mappingField) {
              if (columns.indexOf(i) === -1) {
                columns.push(i);
              }
            }
          }

          if (me.requestField) {
            me.requestField.split(',').forEach(item => {
              if (item && columns.indexOf(item)) {
                columns.push(item);
              }
            });
          }
          columns = columns.distinctKey(columns);

          payload.columns = columns.join(',');

          me.lastRequestParam = payload;

          if (typeof me.buildFilter == 'function') {
            me.buildFilter(payload);
          }
          me.loading = true;
          // trường hợp có query string ko cần phải lấy selected item
          if (me.modelValue && !queryString) {
            Object.assign(payload, {
              selectedItem: JSON.stringify({
                [me.valueField]: me.modelValue,
              }),
            });
          }
          me.$emit(me.$ms.constant.CustomEvent.MsComboboxLoadData, payload);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },
    /**
     * Query dữ liệu local
     * CreatedBy LTDAT 24.06.2020
     */
    doLocalQuery(queryString) {
      const me = this;
      let filterData = me.filterData(queryString);
      if (queryString && me.comboType === 3) {
        // thêm cha vào để build cây
        let roots = [];
        let i = 0;
        while (i < filterData.length) {
          const item = filterData[i];
          const parent = item.parentNode;

          if (!parent || Array.isArray(parent)) {
            roots.push(item);
          } else if (filterData.indexOf(parent) === -1) {
            filterData.push(parent);
          }
          i++;
        }

        // sắp xếp root theo đúng thứ tự
        if (roots.length > 1) {
          const rs = [];
          for (let i = 0; i < me._allData.length; i++) {
            const item = me._allData[i];
            if (roots.indexOf(item) > -1) {
              rs.push(item);

              if (rs.length === roots.length) {
                roots = rs;
                break;
              }
            }
          }
        }

        // build cây
        const rs = [];
        const fnPush = function (item) {
          rs.push(item);
          if (Array.isArray(item.childNodes)) {
            item.childNodes.forEach(child => {
              if (filterData.indexOf(child) > -1) {
                fnPush(child);
              }
            });
          }
        };
        roots.forEach(item => {
          fnPush(item);
        });

        // sắp xếp theo misaCode
        filterData = rs;
      }

      me._filterData = filterData;
      const count = Math.min(me._filterData?.length, me.pageSize);
      const ds = [];
      for (let i = 0; i < count; i++) {
        ds.push(me._filterData[i]);
      }

      me.internalDataSource = ds;
    },
    filterData(queryString) {
      const me = this;
      let matchItem = [];

      if (queryString) {
        me._allData?.forEach(item => {
          let valid = false;
          for (const f in me.filterFields) {
            const value = item[f];
            const test = me.testFilter(me.filterFields[f], value, queryString);
            if (test) {
              valid = true;
              break;
            }
          }

          if (valid) {
            matchItem.push(item);
          }
        });
      } else {
        matchItem = me._allData;
      }
      return matchItem;
    },

    /**
     * Lấy chuỗi cần query, trường hợp number thì lấy value bỏ qua ký tự phân cách
     * CreatedBy LTDAT 11/12/2019
     */
    getQueryString() {
      return (this.internalText || '').trim();
    },
    // Click ra ngoài combo đóng combo lại
    // Created by LTDAT 18.06.2020
    click_out(e) {
      const me = this;
      if (
        !commonFn.hasParent(e.target, me.$el) &&
        me.$refs.dropdownPanel &&
        me.$refs.dropdownPanel.$el &&
        !commonFn.hasParent(e.target, me.$refs.dropdownPanel.$el)
      ) {
        me.collapse();
      }
    },
    // Reset lại các giá trị trong combobox
    reset() {
      const me = this;
      me.internalText = '';
      me.internalSelectedItem = null;
      me.typeAheadPointer = -1;
      me.internalValue = null;
      me.$emit('update:modelValue', me.internalValue);
      me.$emit('update:display', null);
    },
    /**
     * Set giá trị hiển thị
     * @pram value giá trị hiển thị
     */
    setInternalText(value) {
      const me = this;
      me.internalText = value;
    },
    /**
     * Set value cho combo
     */
    setValue(value, userAction = false) {
      const me = this;
      me.internalValue = value;
      me.$emit('update:modelValue', me.internalValue);
      me.doHightlightItem();
      if (me.typeAheadPointer >= 0) {
        me.setInternalItem(me.internalDataSource[me.typeAheadPointer], userAction);
      }
    },
    // Sự kiện nhập liệu trên input
    // Created by LTDAT (16.06.2020)
    onInput() {
      const me = this;

      me._typing = true;
      if (!me.expanded) {
        me.expand();
        // me.doQueryInternal(me.getQueryString(), true);
      }

      if (!me.internalText) {
        const metaData = {
          oldData: me.internalSelectedItem,
          newData: null,
        };
        me.$emit('selected', metaData);
        me.reset();
      }

      me.doQuery(me.internalText, true);
      me._typing = false;
      if (
        !me.rules.some(x => x.name === me.$ms.constant.ValidateRule.ForceSelection) &&
        me.valueField === me.displayField
      ) {
        me.internalValue = me.internalText;
        if (me.isGridEditor) {
          me.$emit('update:display', me.internalValue);
        } else {
          me.$emit('update:modelValue', me.internalValue);
        }
      }
      me.doHightlightItem(true);
      me.adjustScroll();
    },
    // Sự kiện click vào input
    // Created by NNLAM (19/04/2021)
    onClick(e) {
      this.$emit('click', e);
    },
    // Sự kiện focus vào input
    // Created by LTDAT (16.06.2020)
    onFocus() {
      const me = this;
      me.focused = true;
    },
    // Sự kiện change input
    // Created by LTDAT (16.06.2020)
    // Tạm rem hàm này, chưa hiểu làm gì
    onChange() {
      const me = this;
      // if (me.focused) {
      //   if (me.forceSelection) {
      //     if (me.typeAheadPointer < 0) {
      //       //me.reset();
      //       // let dataEmpty = {};
      //       //   dataEmpty[me.valueField] = null;
      //       //   dataEmpty[me.displayField] = me.internalText;
      //       //   let metaData = {
      //       //     oldData: me.internalSelectedItem,
      //       //     newData: dataEmpty,
      //       //     userAction: false
      //       //   };
      //       //TODO bnduc 9.7.2021: Bỏ raise selectd event đi vì các form đang handle để gán lại initText -> lỗi validate forceSelection
      //       // me.$emit("selected", metaData);
      //       me.$emit('update:modelValue', me.internalValue);
      //     } else {
      //       me.typeAheadPointer = me.filterIndexItemByText();
      //     }
      //   } else {
      //     if (me.valueField == me.displayField) {
      //       me.internalValue = me.internalText;
      //       me.$emit('update:modelValue', me.internalValue);
      //     } else {
      //       me.typeAheadPointer = me.filterIndexItemByText();
      //       if (me.typeAheadPointer < 0) {
      //         me.internalValue = null;
      //         let dataEmpty = {};
      //         dataEmpty[me.valueField] = null;
      //         dataEmpty[me.displayField] = me.internalText;
      //         let metaData = {
      //           oldData: me.internalSelectedItem,
      //           newData: dataEmpty,
      //           userAction: false,
      //         };
      //         me.$emit('selected', metaData);
      //         me.$emit('update:modelValue', me.internalValue);
      //       }
      //     }
      //   }
      // }
    },
    // Sự kiện blur input
    // Created by LTDAT (16.06.2020)
    // pvduy 07/05/2021: sửa lại combobox (đã trao đổi với SA anh BNDuc)
    onBlur(e) {
      const me = this;
      me.focused = false;

      // Comment lại đoạn này do không có nhu cầu tự chọn bản ghi cuối cùng
      // NCPHUOC 07/06/2022
      // nếu không có value mà source chỉ có 1 bản ghi + text hiển thị đang map -> select item này
      // if (
      //   me.internalDataSource.length === 1 &&
      //   me.internalText &&
      //   !me.internalValue
      // ) {
      //   let firstItem = me.internalDataSource[0];
      //   me.setInternalItem(firstItem);
      // }

      this.$nextTick(() => {
        me.validateBlur();
      });
      me.$emit('blur', e);
    },
    // Sự kiện keydown input
    // Created by LTDAT (16.06.2020)
    onKeydown(e) {
      const me = this;
      //e.stopPropagation();
      // Nếu bấm 2 mũi tên lên xuống mà đang mở dropdown thì ko emit ra ngoài để xử lý
      let isEmitOut = true;
      switch (e.which) {
        // arrow down
        case 40:
          {
            if (!me.expanded) {
              // Thêm expand khi thực hiện query dữ liệu mới
              me.expanded = true;
              me.doQueryInternal(null, true);
            } else {
              isEmitOut = false;
              if (me.typeAheadPointer < me.getTotalData() - 1) {
                if (me.notSelectionParent) {
                  const typeAheadPointerNext = me.typeAheadPointer + 1;
                  for (let i = typeAheadPointerNext; i < me.getTotalData(); i++) {
                    me.typeAheadPointer++;
                    me.adjustScroll(typeAheadPointerNext);
                    if (!me.internalDataSource[i].isParent) {
                      break;
                    }
                  }
                } else {
                  me.typeAheadPointer++;
                  me.adjustScroll();
                }
              }
            }
          }
          break;
        // arrow up

        case 38:
          {
            if (me.expanded) {
              isEmitOut = false;
              if (me.typeAheadPointer > 0) {
                if (me.notSelectionParent) {
                  const typeAheadPointerPre = me.typeAheadPointer - 1;
                  for (let i = typeAheadPointerPre; i > 0; i--) {
                    me.typeAheadPointer--;
                    me.adjustScroll(me.typeAheadPointer);
                    if (!me.internalDataSource[i].isParent) {
                      break;
                    }
                  }
                } else {
                  me.typeAheadPointer--;
                  me.adjustScroll();
                }
              }
            }
          }
          break;
        case 13:
          {
            if (me.typeAheadPointer >= 0) {
              me.setInternalItem(me.internalDataSource[me.typeAheadPointer]);
            }
            me.collapse();
          }
          break;
        case 9:
          {
            if (me.expanded) {
              if (me.typeAheadPointer >= 0) {
                me.setInternalItem(me.internalDataSource[me.typeAheadPointer]);
              }
              me.collapse();
            }
          }
          break;
      }
      if (isEmitOut) {
        me.$emit(this.$ms.constant.CustomEvent.MsKeydown, e);
      }
    },
    // Lấy tổng số lượng dữ liệu trong data
    getTotalData() {
      const me = this;
      return me.internalDataSource.length;
    },
    // Bôi màu hightlight cho item
    doHightlightItem(ignoreValue) {
      const me = this;
      let index = -1;

      // Sửa lại code: NhThai 17.5.2024: Sửa trường hợp nếu trả dữ liệu cbb về 0 thì 0 nó sẽ hiểu là false
      me.internalValue =
        me.internalValue != null && me.internalValue != undefined
          ? me.internalValue
          : me.internalText
          ? me.internalText
          : null;
      if (me.internalDataSource && me.internalDataSource.length > 0) {
        index = -1;

        if (!ignoreValue && me.internalValue != null && me.internalValue != undefined) {
          const sources = me._allData || me.internalDataSource;

          // Trước khi load tính toán lại typeAheadPointer nếu cần thiết để scroll di chuyển về dúng vị trí khi show scroller lên
          // Tìm vị trí selectedItem trong source, không lấy selectedIndex vì có thể không chính xác khi source thay đổi
          for (let i = 0; i < sources.length; i++) {
            const item = sources[i];
            if (item[me.valueField] === me.internalValue) {
              index = i;
              break;
            }
          }

          // TODO => Đoạn này ko hiểu để làm gì, tạm rem lại do có lỗi duplicate do đoạn này
          // if (index >= me.internalDataSource.length) {
          //   for (let i = me.internalDataSource.length; i <= index; i++) {
          //     me.internalDataSource.push(sources[i]);
          //   }
          // }
        }

        if (index === -1 && me.internalText) {
          const lowerText = me.internalText.toString().toLowerCase();

          if (me.columns && me.columns.length > 0) {
            for (let i = 0; i < me.internalDataSource.length; i++) {
              const item = me.internalDataSource[i];
              for (let j = 0; j < me.columns.length; j++) {
                const field = me.columns[j].dataField;
                const itemValue = item[field];
                if (typeof itemValue === 'string' && itemValue.toLowerCase().indexOf(lowerText) > -1) {
                  index = i;
                  break;
                }
              }

              if (index > -1) {
                break;
              }
            }
          } else {
            for (let i = 0; i < me.internalDataSource.length; i++) {
              const itemValue = me.internalDataSource[i][me.displayField];
              if (typeof itemValue === 'string' && itemValue.toLowerCase().indexOf(lowerText) > -1) {
                index = i;
                break;
              }
            }
          }
        }
      }

      if (me.internalDataSource.length < index && me.queryMode == 'local') {
        const itemCountPerDropdown = 5;
        const splice = index + itemCountPerDropdown;
        me.internalDataSource = _.cloneDeep(me._allData).splice(0, splice);
      }

      me.typeAheadPointer = index;
    },

    /**
     * Hàm kiểm tra dữ liệu có trong data được lấy lên từ db không
     * NHThai 26.12.2024
     */
    checkRecordExistsInDataSource() {
      const me = this;
      let modelValue = me.modelValue, // giá trị truyền vào v-model
        dataSource = me.data || [], // data lấy từ db
        key = me.valueField || '', // key
        res = true; // mặc định là tồn tại

      let check = dataSource.find(x => x[key] == modelValue);
      if (check == null) {
        res = false;
      }

      return res;
    },

    /**
     * Tìm item theo text truyền vào
     * HTHIEP 01.07.2022
     */
    findItemByText(queryText) {
      if (!queryText) return false;
      if (!this.internalDataSource || this.internalDataSource.length === 0) return false;
      const data = this._allData ? this._allData : this.internalDataSource ? this.internalDataSource : [];
      if (!data || data.length === 0) return false;
      return data.filter(item => {
        let text = item[this.displayField];
        if (item[this.suffixDisplayField]) {
          text = `${item[this.suffixDisplayField]} ${this.seperateChar} ${item[this.displayField]}`;
        }
        if (queryText.toString() === text.toString()) {
          return item;
        }
      });
    },
    // Tìm kiếm item trong list
    // Created by LTDAT(16.06.2020)
    filterIndexItemByText(queryText) {
      const me = this;
      const filters = me.filterFields;
      let value = queryText || me.internalText;
      let index = -1;
      if (filters && value) {
        value = value.toString();
        index = me.internalDataSource.findIndex((item, _index) => {
          let ret = false;
          for (const key in filters) {
            if (filters.hasOwnProperty(key)) {
              const s = filters[key];
              if (s == me.$ms.constant.FilterOperator.Contains) {
                ret =
                  item[key] && item[key].toString() && item[key].toString().toLowerCase().contains(value.toLowerCase());
              } else {
                ret =
                  item[key] &&
                  item[key].toString() &&
                  item[key].toString().toLowerCase().startsWith(value.toLowerCase());
              }
              if (ret) {
                break;
              }
            }
          }
          return ret;
        });
      }
      return index;
    },
    // Lấy cột filter để tìm kiếm local
    // Created by LTDAT 16.06.2020
    getFilterFields() {
      const filter = {};
      const me = this;
      if (this.columns && this.columns.length > 0) {
        for (let i = 0; i < this.columns.length; i++) {
          const col = this.columns[i];
          if (!col.filterOperator) {
            filter[this.displayField] = me.$ms.constant.FilterOperator.Contains;
          }
          if (col.dataField && col.filterOperator) {
            if (this.filterColumns && this.filterColumns.length > 0) {
              this.filterColumns.forEach(e => {
                if (!filter.hasOwnProperty(e)) {
                  filter[e] = col.filterOperator;
                }
              });
            } else {
              filter[col.dataField] = col.filterOperator;
            }
          }
        }
      } else {
        filter[this.displayField] = me.$ms.constant.FilterOperator.Contains;
      }
      return filter;
    },
    // Lấy element scroll
    // Created by LTDAT (16.06.2020)
    getScroller() {
      if (this.expanded) {
        // return this.$refs["scroller"];
        const dd = this.$refs['dropdownPanel'];
        if (dd && dd.$el && typeof dd.$el.querySelector === 'function') {
          return dd.$el.querySelector('.scroller-overlay');
        }
      }
      return null;
    },
    /**
     * Di chuyển view đến bản ghi nếu cần
     * @param {Bản ghi cần view} index
     * CreatedBy LTDAT 16.06.2020
     */
    adjustScroll(typeAheadPointer = this.typeAheadPointer) {
      const row = this.$refs['dropdownPanel']?.$el?.querySelector('.combobox-item');
      if (row) {
        const rowHeight = row.getBoundingClientRect().height;
        const scroller = this.getScroller();
        if (scroller) {
          // scroller.scrollToItem(typeAheadPointer, true);
          scroller.scrollTop = (Number.parseInt(rowHeight) - 1) * typeAheadPointer;
        }
      }
    },
    onScrollerVisible() {
      const me = this;
      me.adjustScroll();
    },
    // Hàm xử lý khi chọn item trên dropdown
    // Created by LTDAT(16.06.2020)
    onItemClick(item, event) {
      const me = this;
      event.preventDefault();
      me.setInternalItem(item, true);
      me.$refs.input.focus();
    },
    // NCPHUOC 13/06/2022: Bỏ qua bước check khác item do có trường hợp chọn rồi nhưng nhập lại text
    // Set giá trị được chọn
    setInternalItem(item, isSelected = false) {
      const me = this;
      if (item) {
        if (me.notSelectionParent && item.isParent) {
          return;
        }
        // Lưu lại thông tin old item
        const oldItems = {};
        oldItems[me.valueField] = me.internalValue;
        oldItems[me.displayField] = me.internalText;

        if (!me.isFreeText) {
          me.internalSelectedItem = item;
          me.internalValue = item[me.valueField];
        }

        // HTHIEP: Trường hợp có suffix thì view mang lên cả suffix
        if (me.suffixDisplayField) {
          me.internalText = `${item[me.suffixDisplayField]} ${me.seperateChar} ${item[me.displayField]}`;
          me.$emit('update:suffixDisplay', item[me.suffixDisplayField]);
        } else {
          me.internalText = item[me.displayField];
        }

        me.$emit('update:display', item[me.displayField]);

        me.$emit('update:modelValue', me.internalValue); // NOTE: Update value sau khi update display mục đích để ghi đè giá trị trong TH là combo Enum
        me.typeAheadPointer = me.findIndexItem(item);

        const metaData = {
          oldData: oldItems,
          newData: item,
          isSelected: isSelected,
        };
        me.$emit('selected', metaData);

        /** Khi chọn item thì mới chạy vào event */
        isSelected && me.$emit('selectItem', metaData);
        me.collapse();
      }
    },
    // Tìm vị trị của item trong data
    findIndexItem(item) {
      const me = this;
      return me.internalDataSource.findIndex(_item => {
        return _item == item;
      });
    },
    // Khởi tạo dữ liệu tĩnh mặc định
    initStaticData() {
      const me = this;
      me.comboType = 1;

      me.internalQueryDelay = 200;
      me.filterFields = me.getFilterFields();
      me.internalValue = null;
      me.firstExpand = true;
    },
    /**
     * thêm bản ghi chọn tất cả vào source
     */
    appendSelectAllItem() {
      const me = this;
      if (!me.showSelectAll) {
        return;
      }
      const firstItem = me.internalDataSource[0];
      if (firstItem && firstItem[me.valueField] !== me.selectAllValue) {
        const allItem = {};
        allItem[me.valueField] = me.selectAllValue;
        allItem[me.displayField] = me._selectAllText;

        // vvkiet - 24.06.2021: Bổ sung text Tất cả cho tất cả các cột
        me.columns.forEach(column => {
          allItem[column.dataField] = me._selectAllText;
        });

        me.internalDataSource.unshift(allItem);
      }
    },
    // append và check đã tồn tại chưa
    appendData(data, appendHead = false) {
      const dataQuery = this.queryMode === 'remote' ? this.internalDataSource : this._allData;
      const validData = data.filter(x => !dataQuery.some(y => y[this.valueField] === x[this.valueField]));
      appendHead ? this.internalDataSource.unshift(...validData) : this.internalDataSource.append(validData);
    },
    // Khởi tạo dữ liệu
    initData() {
      const me = this;
      if (me.lastRequestParam && me.lastRequestParam.pageAppend) {
        // if(me.internalDataSource.filter(i=>i==))
        // me.internalDataSource.append(me.data);
        me.appendData(me.data);
        // nếu dữ liệu trả về ít hơn mong đợi -> đánh dấu kết thúc để khi scroll xuống k load thêm nữa
        if (me.data.length < me.pageSize) {
          me.lastRequestParam.end = true;
        } else {
          me.lastRequestParam.end = false;
        }
      } else {
        me.internalDataSource = [...me.data];
        // if (this.internalDataSource.length > 0 && !me.getQueryString()) {
        //   // me.internalDataSource.append(me.data);
        //   me.appendData(me.data);
        // } else {
        // }

        me.appendSelectAllItem();
      }

      if (me.keyField && me.keyField !== me.valueField) {
        commonFn.genVKeyValue(me.internalDataSource, me.keyField);
      }
      // Nếu là combo dạng tree
      if (me.comboType == 3) {
        const data = JSON.parse(JSON.stringify(me.internalDataSource));
        const dataTree = commonFn.nomalizeDataTree(data, me.idProperty || me.valueField, me.parentIdProperty);
        const treeData = commonFn.getBodyData(dataTree);

        if (treeData.length > 0) {
          me._allData = treeData;
        } else {
          me.internalDataSource = [];
          me._allData = null;
        }
      } else if (!me.remoteFilter && me.internalDataSource && me.internalDataSource.length > 0) {
        me._allData = me.internalDataSource;
        me.internalDataSource = [];
      }

      if (me._allData) {
        me.doLocalQuery(me.lastQueryString);
      }
    },
    /**
     * Khởi tạo combo
     *
     * @author hngiap - 01.09.2019
     * 1 là combo không có columns (ComboEditor)
     * 2 là combo có columns
     * 3 là combo tree
     */
    initCombo() {
      const me = this;
      if (me.isTree) {
        me.comboType = 3;
      } else if (me.columns && me.columns.length !== 0) {
        me.comboType = 2;
      } else {
        me.comboType = 1;
      }
    },

    /**
     * click vào icon dropdown
     */
    triggerClick(e) {
      const me = this;
      const expanded = me.expanded;
      // disable thì không làm gì
      if (me.disabled) {
        return;
      }

      if (!expanded) {
        // if (me.pagination) {
        me.doQueryInternal(null, true);
        // } else if (me.firstExpand) {
        //   me.firstExpand = false;

        //   me.doQueryInternal(null, true);
        // }

        // vvkeit - 06.04.2021: 89823 - Lỗi chung: Lỗi trên form điều chuyển hàng loạt dưới bảng chi tiết khi đã chọn tài sản thì đang không xổ combo để chọn tiếp
        // me.$refs.input.focus();
        me.changeDropdownPosition();
        me.doHightlightItem();
      }

      // đặt nextick để tránh lỗi render dom của combobox tree khi chọn item từ dropdown
      me.$nextTick(() => {
        me.expanded = !expanded;
      });

      // focus input
      me.$refs.input.focus();
    },

    /**
     * hiển thị dropdown
     */
    expand() {
      const me = this;

      me.$emit('beforeExpand', me);
      // vvkeit - 06.04.2021: 89823 - Lỗi chung: Lỗi trên form điều chuyển hàng loạt dưới bảng chi tiết khi đã chọn tài sản thì đang không xổ combo để chọn tiếp
      // me.$refs.input.focus();
      me.changeDropdownPosition();
      me.doHightlightItem();

      // đặt nextick để tránh lỗi render dom của combobox tree khi chọn item từ dropdown
      me.$nextTick(() => {
        me.expanded = true;
      });
    },
    /**
     * ẩn dropdown
     */
    collapse() {
      const me = this;
      me.$nextTick(() => {
        me.expanded = false;
      });
    },
    /**
     * Hàm set vị trí cho dropdown menu khi expand
     * TODO: Chưa xử lý khi có scroll
     */
    changeDropdownPosition() {
      const me = this;
      let comboEl = me.$el;

      if (comboEl) {
        comboEl = comboEl.getElementsByClassName('combo-box-input')[0];
      }

      if (comboEl) {
        const comboElRect = comboEl.getBoundingClientRect();
        const comboMenuEl = me.$el;

        const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

        me.dropdownLeftx = comboElRect.left;
        me.dropdownTopx = comboElRect.top + comboElRect.height + 2;
        me.dropdownMaxWidthx = me.dropdownMinWidthx = me.dropdownWidth ? me.dropdownWidth : comboElRect.width;

        // Nếu chạm phải thì align sang trái
        if (comboElRect.left + me.dropdownMaxWidthx > windowWidth) {
          me.dropdownLeftx = comboElRect.right - me.dropdownMaxWidthx;
        }

        if(me.dropdownAlignLeft) {
          me.dropdownLeftx = comboElRect.right - me.dropdownMaxWidthx;
        }

        // Nếu chạm bottom thì align top
        let maxItemsDisplay = me.maxItemsDisplay;
        if (me.columns && me.columns.length > 0) {
          maxItemsDisplay += 1;
        }
        
        // Tính toán cao đúng của dropdown theo cấu hình CSS
        let dropdownHeight;
        
        // Nếu số lượng items ít hơn maxItemsDisplay, tính theo số lượng items thực tế
        // Ngược lại sử dụng giá trị max-height cố định từ CSS (208px)
        if (me.internalDataSource.length < maxItemsDisplay) {
          // Chiều cao theo items + header/footer + padding
          const headerFooterHeight = 40; // Tổng chiều cao dành cho header và footer
          dropdownHeight = me.itemSize * me.internalDataSource.length + headerFooterHeight;
        } else {
          // Sử dụng giá trị max-height của .dropdown-body-container (208px)
          dropdownHeight = 208;
        }
        
        // Kiểm tra space phía dưới
        const spaceBelow = windowHeight - comboElRect.bottom;
        
        // Nếu không đủ không gian phía dưới và có đủ không gian phía trên thì đẩy lên trên
        if (spaceBelow < dropdownHeight && comboElRect.top > dropdownHeight) {
          // Đẩy dropdown lên trên và tạo khoảng cách để không che mất input
          // Trừ đi:
          // 1. Chiều cao của dropdown (dropdownHeight)
          // 2. Chiều cao của combobox input (comboElRect.height) 
          // 3. Thêm 2px padding cho đẹp
          me.dropdownTopx = comboElRect.top - dropdownHeight  - 4;
        }
      }
    },
    /**
     * Click vào icon quicksearch
     */
    quickSearchClick(e) {
      const me = this;
      e.preventDefault();
      if (me.quickSearch) {
        if (me.disabled) {
          return;
        }
        me.expanded = false;
        const param = {...(me.quickSearchParam || {})};
        popupUtil.show(
          me.quickSearch,
          Object.assign(param, {
            /**
             * Sự kiện callback khi chọn item từ form tìm nhanh
             * Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
             */
            options: {
              submit: me.callbackAddData.bind(me),
            },
            close: function () {
              me.$refs.input.focus();
            },
          }),
        );
      }
    },

    callbackAddData(record) {
      const me = this;
      let notExistArr = [];
      const checkingData = me._allData ? me._allData : me.internalDataSource;
      if (Array.isArray(record)) {
        notExistArr = record.filter(x => !checkingData.some(y => y[me.valueField] === x[me.valueField]));

        me.setInternalItem(record[0]);
      } else {
        if (!checkingData.find(i => i[me.valueField] === record[me.valueField])) {
          notExistArr = [record];
        }

        me.setInternalItem(record);
      }
      // me.$nextTick(() => {
      //   me.validate(me);
      // });

      me.$refs.input.focus();
      // Thêm data vào luôn dữ liệu hiện tại
      if (!me.isFreeText) {
        me.internalDataSource.unshift(...notExistArr);
        if (!me.lastRequestParam) {
          me.lastRequestParam = {pageAppend: true};
        } else {
          me.lastRequestParam.pageAppend = true;
        }
      }

      // if (me.existAdded && notExistArr[0]) {
      //   me.existAdded.push(notExistArr[0][me.valueField]);
      // } else {
      //   me.existAdded = [notExistArr[0][me.valueField]];
      // }
      // Bỏ event này do đã thực hiện add luôn vào data source
      // me.$emit('submitQuickAdd', notExistArr);
    },
    /**
     * Validate khi blur
     * ntnghia - 11/03/2021
     * bnduc 21.6.2021: Đổi lại thành gọi hàm validate của control thay vì viết scustom như này
     */
    validateBlur() {
      // TODO Tạm thời xóa validate do chưa kiểm soát được
      const me = this;
      if (!me.isFreeText) {
        this.validate(this);
      }
    },
    /**
     * scroll xuống cuối drowndown -> load thêm dữ liệu
     */
    dropdownScrollEnd() {
      const me = this;
      // if (me.pagination) {
      if (me.queryMode === 'remote' && !me._allData) {
        me.scrollDoing = true;
        // remote
        if (me.lastRequestParam && !me.lastRequestParam.end) {
          // TODO bnduc 21.6.2021: ghi nhận tính huống gọi vào đây 2 lần trên máy qc -> thêm kiểm tra này để k lỗi
          if (me.lastRequestParam.skip && me.lastRequestParam.skip === me.internalDataSource.length) {
            return;
          }

          const payload = me.lastRequestParam;
          payload.skip = me.internalDataSource.length;
          payload.pageAppend = true;
          me.$emit(me.$ms.constant.CustomEvent.MsComboboxLoadData, payload);
        }
      } else {
        // local
        const data = me._filterData || me._allData;
        if (data) {
          me.internalDataSource = me.internalDataSource || [];
          const start = me.internalDataSource.length;
          const count = Math.min(data.length, me.pageSize + start);
          // const appendData = data.slice(start, count);
          // me.appendData(appendData);
          for (let i = start; i < count; i++) {
            me.internalDataSource.push(data[i]);
          }
          me.doHightlightItem(); // vcdoan: 23.10.2024 bổ sung đánh highlight dòng được chọn
        }
      }
      // }
    },
    /**
     * xử lý phím esc
     */
    onEsc(e) {
      const me = this;

      // ẩn dropdown và stop event nếu đang expand
      if (me.expanded) {
        me.collapse();
        e.preventDefault();
        e.cancel = true;
      }
    },

    /**
     * xử lý phím enter
     */
    onEnter(e) {
      const me = this;

      if (me.expanded && !me.loading && me.internalDataSource.length === 0) {
        e.preventDefault();
        e.stopPropagation();
      }
    },

    /**
     * Mở form thêm nhanh
     */
    showQuickAdd(isfromEnter, isfromShortKey, formName) {
      const me = this;

      // Gọi đến sự kiện click icon thêm nhanh nhiều object nếu có config quickAddMulti trên combobox và không phải sự kiện click vào menu-item
      if (me.quickAddMulti && !formName) {
        const quickAddMultiSelector = document.querySelector('.quickAddMulti');

        if (!quickAddMultiSelector) {
          return;
        }

        quickAddMultiSelector.click();

        return;
      }

      // Lấy key form thêm nhanh từ config hoặc tham số truyền vào
      const quickAdd = me.quickAdd || formName;

      if (!me.loading) {
        if (!quickAdd) {
          return;
        }
        me.expanded = false;
        const param = {...(me.quickSearchParam || {})};
        param.mode = me.$ms.constant.FormState.Add;
        if (me.internalText) {
          param.quickAddText = me.internalText;
        }

        // Modified By NXCHIEN 01.06.2022 Sửa lại do base xử lý check options.submit
        param.options = {
          submit: me.callbackAddData.bind(me),
        };

        // TODO => show popup quick add ở đây
        popupUtil.show(quickAdd, param);
      }
    },

    /**
     * @override
     */
    customRuleValidateRules(rules) {
      const me = this;
      me.super('customRuleValidateRules', rules);

      if (me.forceSelection && rules.indexOf('forceSelection')) {
        rules.push('forceSelection');
      }
    },

    /**
     * Xử lý paste value
     */
    onPaste(e) {
      let me = this;

      if (me.preventPaste) {
        e.preventDefault();
      }
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsCombobox.scss';
</style>
